import {Controller} from "stimulus";

export default class extends Controller {
  static targets = [
    'dueDateItem',
    'taskLockItem',
    'goalItem',
    'taskItem',
    'taskList',
    'modulesContainer'
  ];

  connect() {
    // This is necessary because when non selected modules are hidden,
    // filterModules() needs to check the new modules loaded from the next page
    this._onPagyChange();
  }

  disconnect() {
    this.observer.disconnect();
  }

  filterModules() {
    const selectedModuleOptions = this._selectedFilters('#program-overview-display-modules');
    const selectedTaskOptions = this._selectedFilters('#program-overview-display-task-types');

    if (!selectedModuleOptions.length && !selectedTaskOptions.length) {
      this._resetModules();
      return;
    }

    let idsToShow;
    const typeIdsToShow = this._idsToShowForModuleType(selectedModuleOptions);
    const taskIdsToShow = this._idsToShowForTaskType(selectedTaskOptions);

    if (selectedModuleOptions.length && selectedTaskOptions.length) {
      idsToShow = typeIdsToShow.intersection(taskIdsToShow);
    } else {
      idsToShow = typeIdsToShow.union(taskIdsToShow);
    }

    let programModules = document.querySelectorAll('.js-overview-module');

    Array.from(programModules).forEach(element => {
      if (idsToShow.has(element.id)) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    });
  }

  _selectedFilters(dropdownId) {
    let selectedElements = document.querySelectorAll(`${dropdownId} .js-selected`);
    let selectedOptions = [];

    selectedElements.forEach((option) => {
      selectedOptions.push(option.dataset.value.toLowerCase());
    });

    return selectedOptions;
  }

  _idsToShowForModuleType(selectedModuleOptions) {
    let idsToShow = new Set();
    let programModules = document.querySelectorAll('.js-overview-module');

    Array.from(programModules).forEach(element => {
      if (selectedModuleOptions.includes(element.dataset.moduleType)) {
        idsToShow.add(element.id);
      }
    });

    return idsToShow;
  }

  _idsToShowForTaskType(selectedTaskOptions) {
    let idsToShow = new Set();

    selectedTaskOptions.forEach((taskType) => {
      document.querySelectorAll(`[id^="js-overview-task-list-item-"][data-task-type="${taskType}"]`).forEach((task) => {
        idsToShow.add(task.closest('.js-overview-module').id);
      });
    })

    return idsToShow;
  }

  toggleOptionData() {
    this.resetOptionData();

    let selectedElement = document.querySelectorAll('#program-overview-toggle-info_menu .js-selected');

    if (selectedElement.length === 0) {
      return;
    }

    let value = selectedElement[0].dataset.value;

    switch (value) {
      case 'all':
        this._toggleAllOptionData();
        break;
      case 'due_date':
        this._toggleDataForItems(this.dueDateItemTargets);
        break;
      case 'task_lock':
        this._toggleDataForItems(this.taskLockItemTargets);
        break;
      case 'goal':
        this._toggleDataForItems(this.goalItemTargets);
        break;
      default:
        break;
    }
  }

  resetOptionData() {
    this._closeAllTaskLists();

    this._resetDataForItems(this.dueDateItemTargets);
    this._resetDataForItems(this.taskLockItemTargets);
    this._resetDataForItems(this.goalItemTargets);
  }

  toggleOptionDataForContentPage(contentPageId) {
    let selectedElement = document.querySelectorAll('#program-overview-toggle-info_menu .js-selected');

    if (selectedElement.length === 0) {
      return;
    }

    let value = selectedElement[0].dataset.value;

    const contentPageCard = document.getElementById(`js-overview-content-page-card-${contentPageId}`);

    let dueDateItems;
    let lockItems;
    let goalItems;

    switch (value) {
      case 'all':
        dueDateItems = this._dueDateItemsForElement(contentPageCard);
        lockItems = this._lockItemsForElement(contentPageCard);
        goalItems = this._goalItemsForElement(contentPageCard);
        const taskItems = this._taskItemsForElement(contentPageCard);

        this._toggleDataForItems(dueDateItems);
        this._toggleDataForItems(lockItems);
        this._toggleDataForItems(goalItems);
        this._toggleDataForItems(taskItems);

        break;
      case 'due_date':
        dueDateItems = this._dueDateItemsForElement(contentPageCard);

        this._toggleDataForItems(dueDateItems);
        break;
      case 'task_lock':
        lockItems = this._lockItemsForElement(contentPageCard);

        this._toggleDataForItems(lockItems);
        break;
      case 'goal':
        goalItems = this._goalItemsForElement(contentPageCard);

        this._toggleDataForItems(goalItems);
        break;
      default:
        break;
    }
  }

  _applyCurrentToggleState() {
    let selectedElement = document.querySelectorAll(
      '#program-overview-toggle-info_menu .js-selected'
    );

    if (selectedElement.length === 0) {
      return;
    }

    let value = selectedElement[0].dataset.value;

    switch (value) {
      case 'all':
        this._toggleAllOptionData({ scroll: false });
        break;
      case 'due_date':
        this._toggleDataForItems(this.dueDateItemTargets, { scroll: false });
        break;
      case 'task_lock':
        this._toggleDataForItems(this.taskLockItemTargets, { scroll: false });
        break;
      case 'goal':
        this._toggleDataForItems(this.goalItemTargets, { scroll: false });
        break;
      default:
        break;
    }
  }

  _toggleAllOptionData({ scroll = true } = {}) {
    this._toggleDataForItems(this.dueDateItemTargets, { scroll });
    this._toggleDataForItems(this.taskLockItemTargets, { scroll });
    this._toggleDataForItems(this.goalItemTargets, { scroll });
    this._toggleDataForItems(this.taskItemTargets, { scroll });
  }

  _toggleDataForItems(items, { scroll = true } = {}) {
    items.forEach((item) => {
      item.classList.remove('hidden');
      this._openAndHighlightClosestTaskList(item, { scroll });
    });
  }

  _resetDataForItems(items) {
    items.forEach((item) => {
      item.classList.add('hidden');
    });
  }

  _resetModules() {
    let programModules = document.querySelectorAll('.js-overview-module');

    Array.from(programModules).forEach(element => {
      element.classList.remove('hidden');
    });
  }

  _onPagyChange() {
    let loadProgressTarget = document.getElementById('js-overview-modules-load-progress');

    this.observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Also works on slow internet connections since it
          // waits for the DOM to finish loadings
          setTimeout(() => {
            this.filterModules();
            this._applyCurrentToggleState();
          }, 0);
        }
      }
    });

    const config = {
      childList: true
    };

    this.observer.observe(loadProgressTarget, config);
  }

  _openAndHighlightClosestTaskList(item, { scroll = true } = {}) {
    const closestTaskList = item.closest('.js-overview-task-list-top');

    if (!closestTaskList.classList.contains('open')) {
      closestTaskList.dispatchEvent(new CustomEvent(
        'promote:highlight-content-page-card', { detail: { scroll } }
      ));

      closestTaskList.querySelector('[data-collapsible-target="collapsible"]').classList.remove('hidden');
      closestTaskList.querySelector('[data-collapsible-target="icon"]').classList.add('rotate-180');
    }

    let container = item.closest('.task-list-item-container');

    if (container) {
      container.classList.add('pb-3');
    }
  }

  _closeAllTaskLists() {
    this.taskListTargets.forEach((list) => {
      if (list.classList.contains('open')) {
        list.querySelector('.js-overview-task-list-icon').click();

      }
    });

    let container = document.querySelectorAll('.task-list-item-container');

    container.forEach((item) => {
      item.classList.remove('pb-3');
    });
  }

  _dueDateItemsForElement(element) {
    return element.querySelectorAll('[data-overview--toggle-info-target="dueDateItem"]');
  }

  _lockItemsForElement(element) {
    return element.querySelectorAll('[data-overview--toggle-info-target="taskLockItem"]');
  }

  _goalItemsForElement(element) {
    return element.querySelectorAll('[data-overview--toggle-info-target="goalItem"]');
  }

  _taskItemsForElement(element) {
    return element.querySelectorAll('[data-overview--toggle-info-target="taskItem"]');
  }
}
